import { Product } from "@tential/ec-gql-schema/models/product"
import { FABRIC_ITEMS } from "~/const/product"
import type { PagePartsProductType } from "~/types/product"

export type BakuneTopProduct = Product & {
  sleevesAndPants?: string
  detail?: string
  colorSort?: number
}

export type SkuColor = {
  name: string
  colorCode1: string
  colorCode2: string
  display_order_number: number
}

export type LineupProduct = {
  document_id: string
  name: string
  fabric: string
  price: number
  slug: string
  image: string
  sex: string
  size: string[]
  colors: SkuColor[]
}

export type LineupProducts = {
  ss: LineupProduct[]
  fw: LineupProduct[]
}

export type SelectedColor = {
  product_id: string
  color: string
  size: string
  color_code: string
  main_image: string
}

export type ProductsWithColor = {
  document_id: string
  name: string
  colors: SelectedColor[]
}

export type RecommendLineupProduct = {
  name: string
  productDevelopments: {
    name: "short-sleeve-short-pants" | "long-sleeve-long-pants" | "one-piece"
    price: number
    season: string
  }[]
  sex: {
    name: string
    size: string[]
    colors: SkuColor[]
  }[]
  images: string[]
  fabric: string
  silhouette: string
}

export type APIResponse = {
  mainProducts: BakuneTopProduct[]
  popularProducts: PagePartsProductType[]
  recommendLineupProducts: RecommendLineupProduct[]
  lineupProducts: LineupProducts
}

export type SearchConditionKey = "sex" | "silhouette" | "fabric" | "sleevesAndPants" | "detail"

export type SearchCondition = {
  name: string
  value: SearchConditionKey
  items: {
    name: string
    value: string
    hidden?: boolean
  }[]
}

export type SearchConditionSelected = {
  sex: string
  silhouette: string
  fabric: string
  sleevesAndPants: string
  detail: string
}

export const baseSearchConditionList: SearchCondition[] = [
  {
    name: "性別",
    value: "sex",
    items: [
      {
        name: "メンズ",
        value: "mens",
      },
      {
        name: "ウィメンズ",
        value: "ladies",
      },
      {
        name: "ユニセックス",
        value: "unisex",
      },
    ],
  },
  {
    name: "シルエット",
    value: "silhouette",
    items: [
      {
        name: "プルオーバー",
        value: "pullover",
      },
      {
        name: "ボタンフロント",
        value: "button-front",
      },
      {
        name: "ワンピース",
        value: "one-piece",
      },
    ],
  },
  {
    name: "素材",
    value: "fabric",
    items: [
      {
        name: "メッシュ",
        value: "mesh",
      },
      {
        name: "ドライ",
        value: "dry",
      },
      {
        name: "3Dドライ",
        value: "3d-dry",
      },
      {
        name: "ガーゼ",
        value: "gauze",
      },
      {
        name: "スウェット",
        value: "sweat",
      },
      {
        name: "裏起毛スウェット",
        value: "lining-sweat",
      },
      {
        name: "パイル",
        value: "pile",
      },
      {
        name: "プレミアムパイル",
        value: "premium-pile",
      },
      {
        name: "ワッフル",
        value: "waffle",
      },
      {
        name: "サテン",
        value: "satin",
      },
      {
        name: "ベロア",
        value: "velor",
      },
      {
        name: "ソフトニット",
        value: "soft-knit",
      },
    ],
  },
  {
    name: "袖とパンツ",
    value: "sleevesAndPants",
    items: [
      {
        name: "長袖xロングパンツ",
        value: "long-sleeve-long-pants",
      },
      {
        name: "半袖xショートパンツ",
        value: "short-sleeve-short-pants",
      },
    ],
  },
  {
    name: "細部のデザイン",
    value: "detail",
    items: [
      {
        name: "クルーネック",
        value: "crew-neck",
      },
      {
        name: "モックネック",
        value: "mock-neck",
      },
      {
        name: "ジョガーパンツ",
        value: "jogger-pants",
      },
      {
        name: "テーパードパンツ",
        value: "tapered-pants",
      },
    ],
  },
]

export type TextileItem = {
  id: string
  name: string
  swiperClass: string
  items: {
    name: string
    image: string
    bakuneImage: string
    title: string
    description: string
    fabric: string
    url?: string
  }[]
}

/**
 * 選択できる絞り込み条件、絞り込んだ商品一覧、選択している条件を返す
 * @param products - 商品一覧
 * @param selected - 現在選択中の条件
 * @param isAutoSelect - 自動選択を行うかどうか
 */
export const getSearchCondition = (
  products: BakuneTopProduct[],
  selected: SearchConditionSelected,
  isAutoSelect: boolean = true,
): { searchConditionList: SearchCondition[]; products: BakuneTopProduct[]; selected: SearchConditionSelected } => {
  let filterProducts = [...products]

  const newList = baseSearchConditionList.map((condition) => {
    // 商品一覧に対して選択できる絞り混み条件を取得する
    let items = []
    if (condition.value === "sex") {
      // 性別は一番最初の選択肢なので全て選択可能
      items = condition.items
    } else {
      items = condition.items.filter((item) => {
        return filterProducts.filter((product) => product[condition.value] === item.value).length > 0
      })
    }
    if (isAutoSelect && !selected[condition.value] && items[0]) {
      // 条件を選択してない場合、一番最初の条件を自動的に選択する
      selected[condition.value] = items[0].value
    }

    // 現在選択している条件で商品を絞り込む
    if (selected[condition.value]) {
      if (condition.value === "sex") {
        // 性別は必ずユニセックスを含む
        const searchSex = ["unisex"]
        if (selected[condition.value] !== "unisex") {
          searchSex.push(selected[condition.value])
        }
        filterProducts = filterProducts.filter((product) => {
          return searchSex.includes(product[condition.value] || "")
        })
      } else {
        filterProducts = filterProducts.filter((product) => {
          return product[condition.value] === selected[condition.value]
        })
      }
    }
    return {
      ...condition,
      items,
    }
  })
  return { searchConditionList: newList, products: filterProducts, selected }
}

export const getSex = (sex: string) => {
  if (sex === "unisex") {
    return "ユニセックス"
  } else if (sex === "mens") {
    return "メンズ"
  } else if (sex === "ladies") {
    return "ウィメンズ"
  }
  return ""
}

export const getFabric = (fabric: string) => {
  return FABRIC_ITEMS.find((fabricItem) => fabricItem.value === fabric)?.key || ""
}

export const getSize = (sizes: string[]) => {
  return sizes.length > 1 ? sizes[0] + "~" + sizes[sizes.length - 1] : sizes[0]
}

export const getSeason = (season: string) => {
  if (season === "ss") {
    return "春夏"
  } else if (season === "fw") {
    return "秋冬"
  } else if (season === "all") {
    return "オールシーズン"
  }
  return ""
}
